/** @jsxImportSource @emotion/react */

import { useState, useCallback } from 'react';
import useRouter from 'use-react-router';
import { css } from '@emotion/react';

import Form from './form';
import { useDispatch } from 'react-redux';
import TableActions from 'components/layouts/table';
import Table, { useTable } from 'common/components/table';
import useDrawer from 'common/components/drawer/useDrawer';
import TreeTitleActions from 'common/components/tree-layout/main/TreeTitleActions';
import DangerousActionModal from 'common/components/modals/DangerousActionModal';
import UploadFiles from '@/library/components/form/inputs/upload-files';

import {
  deletePurchasingStore,
  getNotLinkedPurchasingStores
} from 'store/purchasing-stores/actions';
import check from 'common/assets/svg/common/check-circle-solid.svg';
import error from 'common/assets/svg/common/x-circle.svg';
import SvgRender from 'common/components/general/SvgRender';

import { Row, Col } from 'reactstrap';

import EnumsDropdown from '../Dropdown';
import TopFilters from './TopFilters';
import TextWithTooltip from 'common/components/general/TextWithTooltip';
import ColorViewer from 'common/components/general/ColorViewer';
import linkIcon from 'common/assets/svg/common/link.svg';
import CollectionFilterSelector from 'common/components/table/filters/CollectionFilterSelector';
import StoreGroupsCollectionFilterSelector from 'common/components/table/filters/StoreGroupsCollectionFilterSelector';
import NamesInTooltip from '@/library/components/general/NamesInTooltip';

const PurchasingStoresTable = () => {
  const [active, setActive] = useState();
  const [type, setType] = useState('create');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, data: null });
  const drawer = useDrawer();
  const dispatch = useDispatch();
  const { match } = useRouter();
  const { path, params } = match;
  const { organizationId } = params;
  const isUnlinked = path?.includes('unlinked');

  const fetchTableData = useCallback(
    async params => {
      if (!organizationId) return;

      const updatedParams = {
        ...params,
        organization_id: organizationId,
        filters: [...params.filters, { name: 'linked', operation: '=', value: 'false' }]
      };

      await dispatch(getNotLinkedPurchasingStores(updatedParams));
    },
    [dispatch, organizationId]
  );

  const table = useTable({
    label: isUnlinked ? 'purchasing_stores_unlinked' : 'purchasing_stores',
    ...(isUnlinked ? { requestTableListFrom: fetchTableData } : {}),
    urlRefetchLevel: 3,
    columns: [
      {
        header: 'Group',
        key: 'store_group_id',
        type: 'collection',
        component: StoreGroupsCollectionFilterSelector,
        canFilter: !isUnlinked,
        sort: true,
        width: 2
      },
      {
        header: 'Code',
        key: 'code',
        type: 'string',
        sort: true,
        width: 1
      },
      {
        header: 'Name',
        key: 'description',
        type: 'string',
        sort: true,
        width: 3
      },
      { header: '', key: 'attachment', minWidth: 45, maxWidth: 45 },
      {
        header: 'Unit',
        key: 'unit_id',
        type: 'collection',
        component: CollectionFilterSelector,
        componentRest: {
          memoizedRequestParams: { path: '/lists', params: { list: 'purchasing-units' } }
        },
        sort: true,
        width: 1
      },
      {
        header: 'Color',
        key: 'color',
        className: 'd-flex align-items-center',
        canFilter: false,
        minWidth: 60,
        maxWidth: 60
      },
      {
        header: 'IHM Related',
        key: 'ihm_related',
        type: 'boolean',
        options: [
          { label: 'Yes', value: 'true' },
          { label: 'No', value: 'false' }
        ],
        className: 'd-flex align-items-center',
        minWidth: 90,
        maxWidth: 90
      },
      isUnlinked
        ? { header: '', hidden: true }
        : {
            header: (
              <div>
                <div>Imported to/</div>
                <div>Linked With Companies</div>
              </div>
            ),
            key: 'companies',
            type: 'string',
            sort: false,
            canFilter: false
          },
      isUnlinked
        ? {
            header: 'Actions',
            key: 'actions',
            field: 'actions'
          }
        : { header: '', hidden: true }
    ]
  });

  const getActions = useCallback(
    row => [
      {
        name: 'Edit',
        onClick: () => {
          setActive(row);
          setType('edit');
          drawer.open();
        }
      },
      {
        name: 'Import to company',
        onClick: () => {
          setActive(row);
          setType('import');
          drawer.open();
        }
      },
      {
        name: 'Equivalent Items',
        onClick: () => {
          setType('equivalent');
          setActive(row);
          drawer.open();
        }
      },
      {
        name: "Link with Company's Existing",
        onClick: () => {
          setType('link');
          setActive(row);
          drawer.open();
        }
      },
      {
        name: 'Delete',
        onClick: () => {
          setActive(row);
          setDeleteModal({ isOpen: true, data: row });
        }
      }
    ],
    [drawer]
  );

  const onDelete = async id => {
    if (!deleteModal?.data?.id) return;

    try {
      await dispatch(deletePurchasingStore(deleteModal?.data)).unwrap();
      setDeleteModal({ isOpen: false, data: null });
      table.refetchData();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <Row className="align-items-center mb-4">
        <Col xs={12}>
          <TableActions
            documentTitle="Purchasing Stores"
            title="Enums"
            className="mt-0"
            dropdown={<EnumsDropdown />}
            actions={[
              isUnlinked
                ? {}
                : {
                    type: 'create',
                    color: 'primary',
                    text: 'Create New',
                    className: 'me-3 ms-1 px-2',
                    onClick: () => {
                      setType('create');
                      drawer.open();
                    }
                  }
            ]}
          />
        </Col>
      </Row>

      <Table
        loader
        topFiltersComponent={<TopFilters />}
        rows={{
          store_group_id: data => (
            <TextWithTooltip className="d-flex align-items-center">
              {data?.store_group?.code && !data?.store_group?.name.includes(data?.store_group?.code)
                ? `${data?.store_group?.code}. `
                : ''}
              {data?.store_group?.name || '-'}
            </TextWithTooltip>
          ),
          code: data => (
            <TextWithTooltip className="d-flex align-items-center">
              {data?.code || '-'}
            </TextWithTooltip>
          ),
          description: data => (
            <TextWithTooltip className="d-flex align-items-center">
              {data?.description || '-'}
            </TextWithTooltip>
          ),
          unit_id: data => (
            <TextWithTooltip className="d-flex align-items-center">
              {data?.purchasing_unit?.name || '-'}
            </TextWithTooltip>
          ),
          color: data => (
            <div className="d-flex align-items-center">
              {data?.color_hex_code ? (
                <ColorViewer color={`#${data?.color_hex_code}`} hideLabel />
              ) : null}
            </div>
          ),
          attachment: data => {
            const photo = data?.photo;
            if (!photo) return;

            return <UploadFiles group="stores" files={[photo]} size="xs" viewOnly />;
          },
          ihm_related: data => (
            <div
              className="d-flex align-items-center"
              css={css`
                ${
                  data?.ihm_related
                    ? `.svg-wrap {
                  border-radius: 50%;
                  box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0.2);
                }`
                    : null
                }
              `}
            >
              <SvgRender
                src={data?.ihm_related ? check : error}
                style={{ width: 16, height: 16 }}
                className={`cme-12 ${data?.ihm_related ? 'text-turquoise' : 'text-violet'}`}
              />
            </div>
          ),
          companies: data => {
            return (
              <div className="d-flex align-items-center">
                <div className="flex-1 d-flex align-items-center">
                  <NamesInTooltip
                    target={`linked-organizations`}
                    names={data?.linked_organizations || []}
                  />
                </div>
                {isUnlinked ? null : <TreeTitleActions actions={getActions(data) || []} />}
              </div>
            );
          },
          actions: data => ({
            options: [
              {
                label: 'Link with Ocean',
                icon: linkIcon,
                onClick: (_, row) => {
                  setType('ocean-link');
                  setActive(row);
                  drawer.open();
                }
              }
            ]
          })
        }}
        {...table}
      />
      <Form drawer={drawer} active={active} type={type} refetchData={table.refetchData} />

      <DangerousActionModal
        transparent
        action={'Delete Account'}
        onAccept={onDelete}
        closeModal={() => setDeleteModal({ isOpen: false, data: null })}
        isOpen={deleteModal?.isOpen}
        actionText={'DELETE'}
        header={'Delete'}
        body={<div>Are you sure you want to delete this store?</div>}
      />
    </div>
  );
};

export default PurchasingStoresTable;
